export interface PasswordRequirement {
  label: string;
  met: boolean;
}

const COMMON_FRAGMENTS = [
  'password',
  'password1',
  'password12',
  'password123',
  'admin',
  'admin123',
  'acceliqa',
  'accelirate',
  'welcome',
  'welcome1',
  'welcome123',
  'changeme',
  'letmein',
  'qwerty',
  'abc123',
  'test123',
];

function identityFragments(values: Array<string | undefined | null>): string[] {
  const fragments = new Set<string>();
  values.forEach((value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) {
      return;
    }
    normalized
      .replace(/[@_-]/g, '.')
      .split('.')
      .forEach((piece) => {
        if (piece.length >= 4) {
          fragments.add(piece);
        }
      });
  });
  return Array.from(fragments);
}

export function evaluatePasswordRequirements(
  password: string,
  identityValues: Array<string | undefined | null> = [],
): PasswordRequirement[] {
  const compact = password.toLowerCase().replace(/[^a-z0-9]/g, '');
  const lowered = password.toLowerCase();
  const containsIdentity = identityFragments(identityValues).some((fragment) => lowered.includes(fragment));

  return [
    { label: 'At least 12 characters', met: password.length >= 12 },
    { label: 'Uppercase and lowercase letters', met: /[A-Z]/.test(password) && /[a-z]/.test(password) },
    { label: 'At least one number', met: /[0-9]/.test(password) },
    { label: 'At least one special character', met: /[^A-Za-z0-9]/.test(password) },
    { label: 'No leading or trailing spaces', met: password === password.trim() },
    {
      label: 'No common words or obvious sequences',
      met: !COMMON_FRAGMENTS.some((fragment) => compact.includes(fragment))
        && !/(123456|654321|abcdef|qwerty)/.test(compact)
        && !/(.)\1{3,}/.test(password),
    },
    { label: 'Does not contain your email, username, or name', met: !containsIdentity },
  ];
}

export function passwordPolicyError(
  password: string,
  identityValues: Array<string | undefined | null> = [],
): string | null {
  const firstUnmet = evaluatePasswordRequirements(password, identityValues).find((requirement) => !requirement.met);
  return firstUnmet ? `Password must meet this requirement: ${firstUnmet.label}.` : null;
}
